'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import BottomNav from '@/components/BottomNav';
import PageHeader from '@/components/PageHeader';
import EventBanner from '@/components/EventBanner';
import Image from 'next/image';
import { GiftIcon } from '@heroicons/react/24/outline';

interface RafflePrize {
  id: string;
  title: string;
  description: string | null;
  donated_by: string | null;
  prize_value: string | null;
  image_url: string | null;
  sort_order: number;
}

export default function RafflePrizesPage() {
  const [prizes, setPrizes] = useState<RafflePrize[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('raffle_prizes')
          .select('id, title, description, donated_by, prize_value, image_url, sort_order')
          .eq('visible', true)
          .order('sort_order', { ascending: true });
        if (error) throw error;
        setPrizes(data || []);
      } catch {
        // silently fail — show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchPrizes();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <EventBanner />
      <main className="pb-nav">
        {/* Hero header */}
        <div className="gradient-hero px-4 pt-6 pb-10">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-primary/20 rounded-xl p-2">
                <GiftIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Raffle Prizes</h1>
                <p className="text-white/60 text-sm">Win amazing prizes at the Fun Day raffle!</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-4 pb-8">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : prizes.length === 0 ? (
            <div className="bg-card rounded-2xl p-8 border border-border card-shadow text-center mt-4">
              <GiftIcon className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-foreground font-semibold mb-1">Prizes Coming Soon</p>
              <p className="text-muted-foreground text-sm">Check back closer to the event for the full prize list!</p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {prizes.map((prize) => (
                <div
                  key={prize.id}
                  className="bg-card rounded-2xl border border-border card-shadow overflow-hidden"
                >
                  {prize.image_url && (
                    <div className="relative w-full h-44">
                      <Image
                        src={prize.image_url}
                        alt={`${prize.title} raffle prize`}
                        fill
                        className="object-cover"
                      />
                      {prize.prize_value && (
                        <div className="absolute top-3 right-3 gradient-primary text-secondary text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                          {prize.prize_value}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-base font-bold text-foreground leading-tight">{prize.title}</h3>
                      {!prize.image_url && prize.prize_value && (
                        <span className="gradient-primary text-secondary text-xs font-bold px-2.5 py-1 rounded-full shrink-0">
                          {prize.prize_value}
                        </span>
                      )}
                    </div>
                    {prize.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3">{prize.description}</p>
                    )}
                    {prize.donated_by && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Donated by</span>
                        <span className="text-xs font-bold text-accent">{prize.donated_by}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 text-center">
                <p className="text-sm font-bold text-foreground mb-1">🎟️ Get Your Raffle Tickets</p>
                <p className="text-xs text-muted-foreground">Tickets available on the day at the event — good luck!</p>
              </div>
            </div>
          )}
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
